import { supabase } from "@/lib/supabase"
import type { Pick } from "@/lib/store/usePickStore"

interface FetchPicksOptions {
    filterId?: string
    type?: 'folder' | 'collection' | 'hashtag' | 'unsorted' | 'favourites'
    pageParam?: number
}

const PAGE_SIZE = 50

const getAccessibleCollectionIds = async (userId: string): Promise<string[]> => {
    const [{ data: collabData }, { data: ownedData }] = await Promise.all([
        supabase.from('collection_collaborators').select('collection_id').eq('user_id', userId),
        supabase.from('collections').select('id').eq('user_id', userId)
    ])
    const collabCollectionIds = (collabData || []).map(r => r.collection_id)
    const ownedCollectionIds = (ownedData || []).map(r => r.id)
    return [...collabCollectionIds, ...ownedCollectionIds]
}

export const fetchPicks = async ({ filterId, type = 'folder', pageParam = 0 }: FetchPicksOptions = {}): Promise<{ picks: Pick[], hasMore: boolean }> => {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('Not authenticated')

    const from = pageParam * PAGE_SIZE
    const to = from + PAGE_SIZE - 1

    let fetchedPicks: any[] = []
    let currentFetchSize = 0

    if (filterId && type !== 'hashtag') {
        const { data, error } = await supabase
            .from('pick_memberships')
            .select(`
                pick_id,
                collection_id,
                folder_id,
                created_at,
                picks:picks(*, pick_visits(last_visited_at))
            `)
            .eq(type === 'collection' ? 'collection_id' : 'folder_id', filterId)
            .order('created_at', { ascending: false })
            .range(from, to)

        if (error) throw error

        currentFetchSize = data?.length || 0
        fetchedPicks = (data || []).map(row => ({
            ...row.picks,
            _membership_collection_id: row.collection_id,
            _membership_folder_id: row.folder_id,
            collection_added_at: row.created_at,
        })).filter((p: any) => p.id)

    } else if (type === 'unsorted') {
        const { data, error } = await supabase
            .from('pick_memberships')
            .select(`
                pick_id,
                collection_id,
                folder_id,
                created_at,
                picks:picks!inner(*, pick_visits(last_visited_at))
            `)
            .eq('user_id', user.id)
            .is('collection_id', null)
            .is('folder_id', null)
            .order('created_at', { ascending: false })
            .range(from, to)

        if (error) throw error

        currentFetchSize = data?.length || 0
        fetchedPicks = (data || []).map(row => row.picks).filter((p: any) => p?.id)

    } else if (type === 'favourites') {
        const allAccessibleCollectionIds = await getAccessibleCollectionIds(user.id)
        const { data, error } = await supabase
            .from('pick_memberships')
            .select(`
                pick_id,
                collection_id,
                folder_id,
                created_at,
                picks:picks!inner(*, pick_visits(last_visited_at))
            `)
            .or(`user_id.eq.${user.id}${allAccessibleCollectionIds.length > 0 ? `,collection_id.in.(${allAccessibleCollectionIds.join(',')})` : ''}`)
            .eq('picks.is_favourite', true)
            .order('created_at', { ascending: false })
            .range(from, to)

        if (error) throw error

        currentFetchSize = data?.length || 0
        
        const uniquePicks = new Map<string, any>()
        ;(data || []).forEach((row: any) => {
            if (!row.picks) return
            if (!uniquePicks.has(row.pick_id)) {
                uniquePicks.set(row.pick_id, {
                    ...row.picks,
                    memberships: []
                })
            }
        })
        fetchedPicks = Array.from(uniquePicks.values())

    } else {
        const allAccessibleCollectionIds = await getAccessibleCollectionIds(user.id)

        let query = supabase
            .from('pick_memberships')
            .select(`
                pick_id,
                collection_id,
                folder_id,
                created_at,
                picks:picks!inner(*, pick_visits(last_visited_at))
            `)
            .or(`user_id.eq.${user.id}${allAccessibleCollectionIds.length > 0 ? `,collection_id.in.(${allAccessibleCollectionIds.join(',')})` : ''}`)

        if (type === 'hashtag' && filterId) {
            query = query.contains('picks.hashtags', [filterId])
        }

        const { data: membershipData, error: fetchError } = await query
            .order('created_at', { ascending: false })
            .range(from, to)

        if (fetchError) throw fetchError

        currentFetchSize = membershipData?.length || 0
        
        const uniquePicks = new Map<string, any>()
        
        ;(membershipData || []).forEach((row: any) => {
            if (!row.picks) return
            
            if (!uniquePicks.has(row.pick_id)) {
                uniquePicks.set(row.pick_id, {
                    ...row.picks,
                    homepageSortDate: row.created_at,
                    memberships: []
                })
            }
            
            const p = uniquePicks.get(row.pick_id)
            p.memberships.push({
                collection_id: row.collection_id,
                folder_id: row.folder_id,
                created_at: row.created_at
            })
        })
        
        fetchedPicks = Array.from(uniquePicks.values())
    }

    const formattedPicks: Pick[] = fetchedPicks.map((p) => {
        const ageInSeconds = (Date.now() - new Date(p.created_at).getTime()) / 1000;
        const isSkeleton = (!p.title || p.title === 'Untitled' || p.title === p.url || p.platform === 'generic') && ageInSeconds < 15;

        return {
            id: p.id,
            url: p.url,
            title: p.title,
            description: p.description,
            image: p.image_url || p.image_original_url,
            siteName: p.site_name,
            favicon: p.favicon,
            platform: p.platform,
            hashtags: p.hashtags || [],
            author: p.author_name,
            published_at: p.published_at,
            createdAt: p.created_at,
            updatedAt: p.updated_at,
            lastVisitedAt: p.pick_visits?.[0]?.last_visited_at || p.last_visited_at,
            collectionId: filterId ? p._membership_collection_id : (p.memberships?.[0]?.collection_id),
            collectionAddedAt: p.collection_added_at,
            folderId: filterId ? p._membership_folder_id : (p.memberships?.[0]?.folder_id),
            memberships: filterId ? [{
                collectionId: p._membership_collection_id,
                folderId: p._membership_folder_id,
                createdAt: p.collection_added_at || p.created_at
            }] : p.memberships?.map((m: any) => ({
                collectionId: m.collection_id,
                folderId: m.folder_id,
                createdAt: m.created_at || p.created_at
            })),
            userId: p.user_id,
            clientId: p.id,
            isLoading: isSkeleton,
            isFavourite: p.is_favourite,
            homepageSortDate: p.homepageSortDate
        };
    })

    return {
        picks: formattedPicks,
        hasMore: currentFetchSize === PAGE_SIZE
    }
}

export interface PickStats {
    totalPicksCount: number
    unsortedCount: number
    favouritesCount: number
}

export const fetchPickStats = async (): Promise<PickStats> => {
    const { data: { session } } = await supabase.auth.getSession()
    const user = session?.user ?? (await supabase.auth.getUser()).data.user
    if (!user) throw new Error('Not authenticated')

    const [totalRes, unsortedRes, favouritesRes] = await Promise.all([
        supabase
            .from('picks')
            .select('*', { count: 'exact', head: true })
            .eq('user_id', user.id),
        supabase
            .from('pick_memberships')
            .select('*', { count: 'exact', head: true })
            .eq('user_id', user.id)
            .is('collection_id', null)
            .is('folder_id', null),
        supabase
            .from('picks')
            .select('*', { count: 'exact', head: true })
            .eq('user_id', user.id)
            .eq('is_favourite', true)
    ])

    return {
        totalPicksCount: totalRes.count ?? 0,
        unsortedCount: unsortedRes.count ?? 0,
        favouritesCount: favouritesRes.count ?? 0,
    }
}
